import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Shield } from "lucide-react";

const CONSENT_KEY = "stansbury_cookie_consent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    const isInternal = window.location.pathname.startsWith("/ops");
    if (!stored && !isInternal) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (type) => {
    let consent;
    if (type === "all") {
      consent = { necessary: true, analytics: true, marketing: true, timestamp: new Date().toISOString() };
    } else if (type === "necessary") {
      consent = { necessary: true, analytics: false, marketing: false, timestamp: new Date().toISOString() };
    } else {
      consent = { ...preferences, necessary: true, timestamp: new Date().toISOString() };
    }
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] p-4 md:p-6" data-testid="cookie-consent">
      <div className="max-w-3xl mx-auto bg-white border border-slate-200 shadow-lg">
        {!showDetails ? (
          <div className="p-5 md:p-6">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="w-5 h-5 text-[#0033CC] mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-1">We value your privacy</h3>
                <p className="text-xs text-slate-500 font-['IBM_Plex_Sans'] leading-relaxed">
                  We use cookies to enhance your experience, analyze site traffic, and for marketing purposes. By clicking "Accept All", you consent to our use of cookies. You can manage your preferences or learn more in our{" "}
                  <a href="/privacy" className="text-[#0033CC] hover:underline">Privacy Policy</a>.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => saveConsent("all")} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-9 px-5 text-xs font-['Outfit'] font-medium" data-testid="cookie-accept-all">Accept All</Button>
              <Button onClick={() => saveConsent("necessary")} variant="outline" className="rounded-none h-9 px-5 text-xs font-['Outfit']" data-testid="cookie-necessary-only">Necessary Only</Button>
              <button onClick={() => setShowDetails(true)} className="text-xs text-[#0033CC] font-['Outfit'] hover:underline ml-2" data-testid="cookie-manage">Manage Preferences</button>
            </div>
          </div>
        ) : (
          <div className="p-5 md:p-6">
            <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-4">Cookie Preferences</h3>
            <div className="space-y-3 mb-5">
              <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100">
                <div>
                  <p className="text-xs font-['Outfit'] font-medium text-[#0F172A]">Strictly Necessary</p>
                  <p className="text-[10px] text-slate-400 font-['IBM_Plex_Sans']">Required for the site to function. Cannot be disabled.</p>
                </div>
                <span className="text-[10px] font-['JetBrains_Mono'] text-emerald-600 bg-emerald-50 px-2 py-0.5">Always On</span>
              </div>
              <label className="flex items-center justify-between p-3 border border-slate-200 cursor-pointer hover:bg-slate-50">
                <div>
                  <p className="text-xs font-['Outfit'] font-medium text-[#0F172A]">Analytics</p>
                  <p className="text-[10px] text-slate-400 font-['IBM_Plex_Sans']">Help us understand how visitors interact with our site.</p>
                </div>
                <input type="checkbox" checked={preferences.analytics} onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })} className="w-4 h-4 accent-[#0033CC]" data-testid="cookie-toggle-analytics" />
              </label>
              <label className="flex items-center justify-between p-3 border border-slate-200 cursor-pointer hover:bg-slate-50">
                <div>
                  <p className="text-xs font-['Outfit'] font-medium text-[#0F172A]">Marketing</p>
                  <p className="text-[10px] text-slate-400 font-['IBM_Plex_Sans']">Used to deliver relevant advertisements and track campaigns.</p>
                </div>
                <input type="checkbox" checked={preferences.marketing} onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })} className="w-4 h-4 accent-[#0033CC]" data-testid="cookie-toggle-marketing" />
              </label>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => saveConsent("custom")} className="bg-[#0033CC] text-white hover:bg-[#002299] rounded-none h-9 px-5 text-xs font-['Outfit'] font-medium" data-testid="cookie-save-prefs">Save Preferences</Button>
              <Button onClick={() => setShowDetails(false)} variant="outline" className="rounded-none h-9 px-5 text-xs font-['Outfit']">Back</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
